import type { QueryClient } from "@tanstack/react-query";
import { clearCompanyProductsCache } from "./companyProductsCache";
import { idbKvDel } from "../stores/indexedDbStringKv";
import { useProfileStore } from "../stores/profileStore";
import { useBrandExplorerStore } from "../stores/brandExplorerStore";
import { useTrackerStore } from "../stores/trackerStore";
import { useFilterStore } from "../stores/filterStore";
import { useSocialAdminStore } from "../stores/socialAdminStore";
import { usePodcastLearningsStore } from "../stores/podcastLearningsStore";
import { useExplorerStore } from "../stores/explorerStore";

/** Theme preference — not tied to a company session. */
export const PRESERVED_LOCAL_STORAGE_KEYS = ["podscope-color-mode"] as const;

/** Zustand persist keys and other session-scoped localStorage entries. */
export const SESSION_LOCAL_STORAGE_KEYS = [
  "profile-filters",
  "tracker-date-filter",
  "brand-explorer-state",
  "podcast-analysis-filters",
  "social-admin-filters",
  "podcast-learnings-filters",
  "company_products_cache",
  "explorer-tab",
] as const;

/** explorer-livestream-ui kept for one-time legacy cleanup after rankingMode moved into explorer-store */
const SESSION_IDB_KEYS = ["explorer-store", "explorer-livestream-ui"] as const;

function removeSessionLocalStorageKeys(): void {
  for (const key of SESSION_LOCAL_STORAGE_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
}

function resetZustandStores(): void {
  useProfileStore.getState().resetToDefaults();
  useBrandExplorerStore.getState().reset();
  useTrackerStore.getState().resetToDefaults();
  useFilterStore.getState().resetFilters();
  useSocialAdminStore.getState().resetFilters();
  usePodcastLearningsStore.getState().resetFilters();
  useExplorerStore.getState().resetToDefaults();
}

/** Wipe React Query, persisted filters, and company cache on auth boundary changes. */
export function clearAppSessionState(queryClient: QueryClient): void {
  queryClient.clear();
  clearCompanyProductsCache();
  removeSessionLocalStorageKeys();

  void Promise.all(SESSION_IDB_KEYS.map((key) => idbKvDel(key))).catch(
    () => {},
  );

  resetZustandStores();
}
